import { get } from 'lodash';
import { MdCampaign } from 'react-icons/md';
import {
  HEALTH_VARIANTS,
  PASSING_STATUSES,
  SERIES_COLORS,
  SOURCE_LABELS,
  SOURCE_STYLES,
} from './constants';
import {
  AnalyticsSeries,
  BreakdownItem,
  MonitoringAnalyticsResponse,
} from './types';

export function getSeriesColor(index: number) {
  return SERIES_COLORS[index % SERIES_COLORS.length];
}

export function getSourceLabel(source: string) {
  return SOURCE_LABELS[source] || source;
}

export function getSourceStyle(source?: string) {
  return (
    SOURCE_STYLES[source as keyof typeof SOURCE_STYLES] || {
      color: '#6c757d',
      Icon: MdCampaign,
    }
  );
}

export function getTestStatusVariant(status: string) {
  if (PASSING_STATUSES.has(status)) return 'success';
  if (status === 'In Progress' || status === 'Not Picked') return 'warning';
  if (status === 'Unsupported Test') return 'secondary';
  return 'danger';
}

export function formatTimestamp(timestamp?: string | number) {
  if (!timestamp) return 'NA';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return 'NA';

  return date.toLocaleString();
}

export function getCreativePreviewHref(test: any) {
  const params = new URLSearchParams({
    alphCrid: String(get(test, 'testMetadata.crid', '')),
    alphCid: String(get(test, 'testMetadata.cid', '')),
    osv: String(get(test, 'osv', '')),
    testTag: String(get(test, 'testTag', '')),
    mosaicRedirect: 'true',
  });

  return `/creative-preview?${params.toString()}`;
}

export function getHealthSummary(tests: any[]) {
  if (!tests.length) {
    return {
      label: 'No recent tests',
      variant: HEALTH_VARIANTS.unknown,
      percentage: 0,
    };
  }

  const passingCount = tests.filter(test =>
    PASSING_STATUSES.has(get(test, 'status', '')),
  ).length;
  const percentage = Math.round((passingCount / tests.length) * 100);

  if (percentage >= 80) {
    return {
      label: `${passingCount}/${tests.length} passing`,
      variant: HEALTH_VARIANTS.healthy,
      percentage,
    };
  }

  if (percentage >= 50) {
    return {
      label: `${passingCount}/${tests.length} passing`,
      variant: HEALTH_VARIANTS.warning,
      percentage,
    };
  }

  return {
    label: `${passingCount}/${tests.length} passing`,
    variant: HEALTH_VARIANTS.unhealthy,
    percentage,
  };
}

export function getTodayDateString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getMonthStartDateString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

export function getSeriesTotal(series: AnalyticsSeries) {
  return series.values.reduce((sum, item) => sum + item.count, 0);
}

export function getOverallTotal(series: AnalyticsSeries[]) {
  return series.reduce((sum, item) => sum + getSeriesTotal(item), 0);
}

export function getBreakdownTotal(items: BreakdownItem[]) {
  return items.reduce((sum, item) => sum + item.count, 0);
}

export function formatRangeLabel(range?: MonitoringAnalyticsResponse['range']) {
  if (!range?.start || !range?.end) return 'No date range';

  const start = new Date(range.start);
  const end = new Date(range.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'No date range';
  }

  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
}

export function buildVisibleInflowSeries(
  analytics?: MonitoringAnalyticsResponse,
  selectedSource?: string,
) {
  const inflow = analytics?.inflow || [];
  if (!inflow.length) return [];

  if (selectedSource && selectedSource !== 'all') {
    return inflow.filter(item => item.key === selectedSource);
  }

  const buckets = inflow[0].values.map(item => item.bucket);
  const aggregated = buckets.map((bucket, index) => ({
    bucket,
    count: inflow.reduce(
      (sum, item) => sum + (item.values[index]?.count || 0),
      0,
    ),
  }));

  return [{ key: 'all', label: 'All Sources', values: aggregated }];
}
